import { useContext, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from '@mui/material';
import { LocaleContext } from '@/Contexts/LocaleContext';
import { logError } from '@/utils/errorHandlerUtil';

import { DialogTitleWithClose } from './DialogTitleWithClose';
import { SnackbarContext } from '@/Contexts/SnackbarProvider';
import { stringHasValue } from '@/utils/stringUtil';

type ConfirmAction = () => void | Promise<void>;

interface ConfirmationModalProps {
  open: boolean;
  header?: string;
  confirmationText?: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
  onClose: () => void;
  onDelete?: ConfirmAction;
}

export default function ConfirmationModal({
  open,
  header,
  confirmationText,
  confirmButtonText,
  cancelButtonText,
  onClose,
  onDelete,
}: ConfirmationModalProps) {
  const { translatedText } = useContext(LocaleContext);
  const { setAlert } = useContext(SnackbarContext);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirm = async () => {
    if (onDelete == null || isSubmitting) return;

    try {
      setIsSubmitting(true);
      await onDelete();
      onClose();
      if (stringHasValue(translatedText.common.defaultConfirmationModalText.successMessage)) {
        setAlert({
          message: translatedText.common.defaultConfirmationModalText.successMessage,
          severity: 'success',
        });
      }
    } catch (error) {
      logError('Confirmation modal action failed.', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  header = header ?? translatedText.common.defaultConfirmationModalText.header;
  confirmationText =
    confirmationText ?? translatedText.common.defaultConfirmationModalText.confirmationMessage;
  cancelButtonText =
    cancelButtonText ?? translatedText.common.defaultConfirmationModalText.cancelButtonText;
  confirmButtonText =
    confirmButtonText ?? translatedText.common.defaultConfirmationModalText.confirmationButtonText;

  return (
    <Dialog open={open} onClose={isSubmitting ? undefined : onClose} maxWidth='md' fullWidth>
      <DialogTitleWithClose onClose={isSubmitting ? undefined : onClose}>
        {header}
      </DialogTitleWithClose>
      <DialogContent>
        <Typography variant='body1'>{confirmationText}</Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
        <Button variant='secondary' onClick={onClose} disabled={isSubmitting}>
          {cancelButtonText}
        </Button>
        <Button
          variant='primary'
          onClick={handleConfirm}
          disabled={isSubmitting || onDelete == null}
        >
          {isSubmitting ? <CircularProgress size={18} color='inherit' /> : confirmButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
